// ============================================
// ORDER TYPE SELECTOR COMPONENT
// Modal for selecting order type when starting new order
// ============================================

import React, { useState } from 'react';
import { 
  X, 
  UtensilsCrossed, 
  Package, 
  Truck,
  Users,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const ORDER_TYPES = [
  {
    id: 'dineIn',
    name: 'Dine In',
    description: 'Seated guests at this table',
    icon: UtensilsCrossed,
    color: 'bg-brand-orange',
    borderColor: 'border-brand-orange',
    showGuests: true,
  },
  {
    id: 'takeaway',
    name: 'Takeaway',
    description: 'Customer picks up order',
    icon: Package,
    color: 'bg-amber-500',
    borderColor: 'border-amber-500',
    showGuests: false,
  },
  {
    id: 'delivery',
    name: 'Delivery',
    description: 'Order delivered to customer',
    icon: Truck,
    color: 'bg-blue-500',
    borderColor: 'border-blue-500',
    showGuests: false,
  },
];

export function OrderTypeSelector({ table, onSelect, onClose }) {
  const [selectedType, setSelectedType] = useState(table ? 'dineIn' : null);
  const [guests, setGuests] = useState(table?.seats ? Math.min(2, table.seats) : 2);
  const [customerName, setCustomerName] = useState('');

  const handleSelect = () => {
    if (!selectedType) return;
    onSelect(selectedType, table?.id || null, selectedType === 'dineIn' ? guests : 0);
  };

  const selectedConfig = ORDER_TYPES.find(t => t.id === selectedType);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {table ? `Start Order - Table ${table.tableNumber || table.table_number || table.id}` : 'New Order'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Order Type Selection */}
          <div className="grid grid-cols-3 gap-3">
            {ORDER_TYPES.map(type => {
              const Icon = type.icon;
              const isSelected = selectedType === type.id;
              
              // Only show Dine In if table is selected
              if (type.id === 'dineIn' && !table) return null;
              
              return (
                <button
                  key={type.id}
                  data-testid={`order-type-${type.id}`}
                  onClick={() => setSelectedType(type.id)}
                  className={cn(
                    'flex flex-col items-center p-4 rounded-xl border-2 transition-all',
                    'hover:shadow-md hover:-translate-y-0.5',
                    isSelected
                      ? `${type.borderColor} bg-gray-50 shadow-md`
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <div className={cn(
                    'w-14 h-14 rounded-full flex items-center justify-center mb-3',
                    type.color
                  )}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">{type.name}</span>
                  <span className="text-xs text-gray-500 text-center mt-1">
                    {type.description}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Guest Count (for Dine In) */}
          {selectedType === 'dineIn' && table && (
            <div className="bg-gray-50 rounded-lg p-4">
              <Label className="mb-2 block">Number of Guests</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setGuests(Math.max(1, guests - 1))}
                  disabled={guests <= 1}
                >
                  -
                </Button>
                <div className="flex items-center gap-2 px-4">
                  <Users className="w-5 h-5 text-gray-400" />
                  <span className="font-mono text-2xl font-bold w-8 text-center">
                    {guests}
                  </span>
                  <span className="text-gray-400">/ {table.seats}</span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setGuests(Math.min(table.seats, guests + 1))}
                  disabled={guests >= table.seats}
                >
                  +
                </Button>
              </div>
            </div>
          )}

          {/* Customer Name (for Takeaway/Delivery) */}
          {(selectedType === 'takeaway' || selectedType === 'delivery') && (
            <div className="bg-gray-50 rounded-lg p-4">
              <Label className="mb-2 block">Customer Name (optional)</Label>
              <Input
                data-testid="customer-name-input"
                placeholder="e.g., Anders"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            data-testid="btn-start-order"
            onClick={handleSelect}
            disabled={!selectedType}
            className="flex-1 gap-2 bg-brand-orange hover:bg-brand-orange/90"
          >
            Start Order
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
